/**
 * **„Przejrzyj" — the way from a survey task into the whole survey** (owner, 25 September 2026):
 * the client's Ankiety with the submitted occurrence open, every answer beside the previous one.
 * Since 2 October 2026 also „Zobacz check-in", from a low reading a survey reported to the check-in
 * it came from; which of the two a task gets is `model/surveyLink`'s.
 *
 * It stands beside „Oznacz jako przejrzane" and is not it: that one WRITES a mark the client sees
 * (`intake-survey` R31); this one only reads. Drawn as the foot's other read, `SessionLink`, in its
 * text register and its stylesheet.
 *
 * Two ways there, one control: a surface away from the client LINKS to the client's card
 * (`model/taskAction`'s `surveyDestination`); the client's own Przegląd switches its tab in place
 * (`onOpen`), keeping the roster list the trainer walks.
 */

import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { taskLabel } from '../lib/taskLabel';
import type { AttentionItem } from '../model/attentionItems';
import { surveyRead } from '../model/surveyLink';
import { surveyDestination } from '../model/taskAction';
import styles from './SessionLink.module.css';
import { ActGlyphMark } from './TaskActs';

interface SurveyLinkProps {
  readonly item: AttentionItem;
  /** Open the survey on the surface itself — the client's Przegląd. Absent, the control links. */
  readonly onOpen?: (surveyAssignmentId: number) => void;
}

export function SurveyLink({ item, onOpen }: SurveyLinkProps) {
  const { t } = useTranslation();
  const read = surveyRead(item);
  const clientId = item.subjectCustomerId;
  if (read === null || clientId === null) return null;

  const label = t(read.labelKey);
  const name = t('web:home.action.forTask', { action: label, task: taskLabel(item, t) });
  const content = (
    <>
      <ActGlyphMark glyph="survey" />
      {label}
    </>
  );
  return onOpen === undefined ? (
    <Link className={styles.link} {...surveyDestination(clientId, read.surveyAssignmentId)} aria-label={name}>
      {content}
    </Link>
  ) : (
    <button
      type="button"
      className={styles.link}
      aria-label={name}
      onClick={() => {
        onOpen(read.surveyAssignmentId);
      }}
    >
      {content}
    </button>
  );
}
